import React, { useState } from 'react';
import VolunteerIdCard from './VolunteerIdCard';
import { Sparkles, Zap, Target } from 'lucide-react'; // Added lucide-react for icons

const VolunteerIdCardDemo = () => {
  const [showIdCard, setShowIdCard] = useState(false);

  // Sample data for demo
  const sampleVolunteer = {
    _id: 'demo123456789',
    name: 'Rahul Kumar Sharma',
    email: 'rahul.sharma@email.com',
    phone: '+91 98765 43210',
    dateOfBirth: '1995-03-15',
    age: 28,
    gender: 'male',
    bloodGroup: 'O+',
    address: '123, Green Park Colony, Sector 15',
    city: 'Mumbai',
    state: 'Maharashtra',
    country: 'India',
    postalCode: '400001',
    skills: ['First Aid', 'Event Management', 'Teaching', 'Community Outreach'],
    experience: '5 years of community service and disaster relief',
    education: 'Bachelor of Social Work',
    occupation: 'Social Worker',
    emergencyContact: {
      name: 'Mrs. Priya Sharma',
      relationship: 'Mother',
      phone: '+91 98765 43211'
    },
    joinDate: '2022-01-15',
    yearsOfService: 2,
    status: 'Active',
    profilePicture: null,
    formattedDateOfBirth: 'Mar 15, 1995',
    formattedJoinDate: 'Jan 15, 2022',
    formattedCreatedAt: 'Jan 15, 2022'
  };

  const sampleNGO = {
    name: 'BloodHero Foundation',
    logo: null,
    address: '456, Community Center, Andheri West',
    city: 'Mumbai',
    state: 'Maharashtra',
    country: 'India',
    phone: '+91 22 1234 5678',
    email: 'info@bloodhero.org',
    website: 'www.bloodhero.org',
    description: 'Dedicated to saving lives through blood donation and community service',
    registrationNumber: 'NGO-2022-001-MH'
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">
            Volunteer ID Card Demo
          </h1>
          <p className="text-gray-600 text-lg">
            Experience the revolutionary 2025 modern UI design with glassmorphism, advanced gradients, and cutting-edge styling
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6">
            Sample Volunteer Information
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-medium text-gray-700 mb-3">Personal Details</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <p><span className="font-medium">Name:</span> {sampleVolunteer.name}</p>
                <p><span className="font-medium">Age:</span> {sampleVolunteer.age} years</p>
                <p><span className="font-medium">Blood Group:</span> {sampleVolunteer.bloodGroup}</p>
                <p><span className="font-medium">Gender:</span> {sampleVolunteer.gender}</p>
                <p><span className="font-medium">Location:</span> {sampleVolunteer.city}, {sampleVolunteer.state}</p>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-medium text-gray-700 mb-3">Contact & Skills</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <p><span className="font-medium">Phone:</span> {sampleVolunteer.phone}</p>
                <p><span className="font-medium">Email:</span> {sampleVolunteer.email}</p>
                <p><span className="font-medium">Skills:</span> {sampleVolunteer.skills.join(', ')}</p>
                <p><span className="font-medium">Experience:</span> {sampleVolunteer.experience}</p>
              </div>
            </div>
          </div>

          <div className="mt-6 text-center">
            <button
              onClick={() => setShowIdCard(true)}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              🆔 View ID Card
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6">
            2025 Modern UI Design Features
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-medium text-gray-800 mb-2">Glassmorphism Design</h3>
              <p className="text-gray-600 text-sm">
                Modern glass-like effects with backdrop blur, transparency, and sophisticated layering for premium aesthetics
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Zap className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-medium text-gray-800 mb-2">Advanced Gradients</h3>
              <p className="text-gray-600 text-sm">
                Multi-directional gradients, animated backgrounds, and dynamic color schemes for visual impact
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-medium text-gray-800 mb-2">Modern Typography</h3>
              <p className="text-gray-600 text-sm">
                Enhanced readability, improved spacing, and contemporary font choices for professional appearance
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ID Card Modal */}
      {showIdCard && (
        <VolunteerIdCard
          volunteer={sampleVolunteer}
          ngo={sampleNGO}
          isOpen={showIdCard}
          onClose={() => setShowIdCard(false)}
        />
      )}
    </div>
  );
};

export default VolunteerIdCardDemo;
